import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useRole } from '../lib/role';
import { Wordmark } from '../pages/Login';

/** Operator app shell.
 *
 * Three layouts in one:
 *  - **Mobile (<md)**: hidden sidebar; top bar with hamburger opens a
 *    slide-over drawer. Backdrop click + nav click closes it.
 *  - **Desktop, expanded**: full 224px sidebar with labels (legacy
 *    look).
 *  - **Desktop, collapsed**: ~56px icon-only rail; hover shows a
 *    tooltip with the label. State is persisted in localStorage
 *    under `shell.sidebar.collapsed` so it survives reloads.
 */
const COLLAPSE_KEY = 'shell.sidebar.collapsed';

export default function Shell() {
  const loc = useLocation();
  const { isAdmin } = useRole();
  const { data: tenants } = useQuery({
    queryKey: ['tenants', null, null],
    queryFn: () => api.listTenants(),
    refetchInterval: 30_000,
  });

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  // Mobile drawer is a separate piece of state — the desktop
  // collapse pref shouldn't affect whether the drawer is open on phones.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Close the drawer whenever the route changes (operator just navigated).
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const isOnTenants = loc.pathname === '/tenants' || loc.pathname.startsWith('/tenants/');

  const nav = (
    <Nav
      collapsed={collapsed}
      isAdmin={isAdmin}
      isOnTenants={isOnTenants}
      currentPath={loc.pathname}
      tenantCount={tenants?.length}
    />
  );

  return (
    <div className="flex flex-col md:flex-row h-full">
      {/* Mobile top bar — visible only below md. */}
      <header className="md:hidden flex items-center justify-between border-b border-line glass px-3 py-2 shrink-0">
        <button
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          className="p-2 -m-2 text-dim hover:text-ink rounded-md"
        >
          <IconMenu />
        </button>
        <Link to="/tenants" className="hover:opacity-80 transition">
          <Wordmark className="h-5" />
        </Link>
        {/* Spacer so the wordmark stays centered vs the hamburger. */}
        <div className="w-8" />
      </header>

      {/* Backdrop for mobile drawer. */}
      {drawerOpen && (
        <button
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-[2px] z-40"
        />
      )}

      {/* Sidebar. Three width modes:
            mobile (closed)  → hidden
            mobile (open)    → fixed overlay slide-in from left, 240px
            desktop expanded → 224px
            desktop collapsed→ 56px (icon rail) */}
      <aside
        className={[
          'border-line glass flex flex-col shrink-0 transition-[width,transform] duration-200',
          // Mobile: fixed overlay
          'fixed md:static inset-y-0 left-0 z-50 w-[240px] border-r',
          drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          // Desktop width
          collapsed ? 'md:w-[56px]' : 'md:w-[224px]',
        ].join(' ')}
      >
        {/* Brand row — desktop only (mobile has its own top bar).
            Expanded: wordmark on the left, small collapse-toggle on the
            right. Collapsed: just the toggle, centered, which expands
            on click. The icon flips horizontally based on state. */}
        <div className={`hidden md:flex items-center ${collapsed ? 'justify-center px-1.5' : 'justify-between px-4'} pt-5 pb-4`}>
          {!collapsed && (
            <Link to="/tenants" className="hover:opacity-80 transition">
              <Wordmark className="h-5" />
            </Link>
          )}
          <button
            onClick={() => setCollapsed(v => !v)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="p-1.5 -m-1.5 text-dim hover:text-ink rounded-md transition"
          >
            <IconSidebarToggle collapsed={collapsed} />
          </button>
        </div>

        {/* Mobile-only brand inside the drawer (above nav). */}
        <div className="md:hidden flex items-center justify-between px-4 pt-5 pb-4">
          <Link to="/tenants" className="hover:opacity-80 transition">
            <Wordmark className="h-5" />
          </Link>
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigation"
            className="p-1.5 -m-1.5 text-dim hover:text-ink"
          >
            <IconClose />
          </button>
        </div>

        {nav}

        {/* Footer — sign out only. Collapse toggle lives at the top
            next to the wordmark; doubling it here would just be noise. */}
        <div className="border-t border-line px-2 py-2 space-y-0.5">
          <FooterItem
            collapsed={collapsed}
            onClick={async () => { await api.logout(); window.location.href = '/'; }}
            icon={<IconLogout />}
            label="Sign out"
          />
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}


// ── Nav (shared by mobile drawer + desktop rail) ───────────────────────

function Nav({
  collapsed, isAdmin, isOnTenants, currentPath, tenantCount,
}: {
  collapsed: boolean;
  isAdmin: boolean;
  isOnTenants: boolean;
  currentPath: string;
  tenantCount: number | undefined;
}) {
  return (
    <nav className={`flex-1 ${collapsed ? 'px-1.5' : 'px-2'} space-y-0.5`}>
      <NavItem to="/home" collapsed={collapsed}
        active={currentPath === '/home'} icon={<IconHome />} label="Home" />
      <NavItem to="/tenants" collapsed={collapsed}
        active={isOnTenants || currentPath === '/new'}
        icon={<IconGrid />}
        label="Tenants"
        right={tenantCount !== undefined
          ? <span className="text-[10px] tabular-nums text-dim">{tenantCount}</span>
          : undefined}
      />
      <NavItem to="/docs" collapsed={collapsed}
        active={currentPath === '/docs' || currentPath.startsWith('/docs/')}
        icon={<IconBook />} label="Docs" />
      <NavExternal
        href="/api/docs"
        collapsed={collapsed}
        icon={<IconCode />}
        label="API & MCP"
      />
      <NavItem to="/settings" collapsed={collapsed}
        active={currentPath === '/settings'} icon={<IconCog />}
        label="Settings & billing" />
      <NavItem to="/tokens" collapsed={collapsed}
        active={currentPath === '/tokens'} icon={<IconKey />}
        label="API tokens" />
      <NavItem to="/audit-log" collapsed={collapsed}
        active={currentPath === '/audit-log'} icon={<IconClock />}
        label="Audit log" />
      {isAdmin && (
        <>
          <div className="h-px bg-line my-2 mx-1.5" />
          <NavItem to="/clients" collapsed={collapsed}
            active={currentPath === '/clients'} icon={<IconBriefcase />}
            label="Workspaces" />
          <NavItem to="/reconnect" collapsed={collapsed}
            active={currentPath.startsWith('/reconnect')} icon={<IconRefresh />}
            label="Reconnect" />
        </>
      )}
    </nav>
  );
}


function NavItem({
  to, active, icon, label, right, collapsed,
}: {
  to: string; active: boolean; icon: React.ReactNode;
  label: string; right?: React.ReactNode; collapsed: boolean;
}) {
  return (
    <Link
      to={to}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      className={`flex items-center gap-2.5 rounded-md text-sm transition
        ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'}
        ${active
          ? 'bg-accent/15 text-ink shadow-[inset_0_0_0_1px_rgba(139,92,246,0.25)]'
          : 'text-dim hover:text-ink hover:bg-white/[0.03]'}`}
    >
      <span className="shrink-0 text-current opacity-80">{icon}</span>
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {!collapsed && right}
    </Link>
  );
}


function NavExternal({
  href, icon, label, collapsed,
}: {
  href: string; icon: React.ReactNode; label: string; collapsed: boolean;
}) {
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer"
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      className={`flex items-center gap-2.5 rounded-md text-sm text-dim hover:text-ink hover:bg-white/[0.03] transition
        ${collapsed ? 'justify-center px-2 py-2.5' : 'justify-between px-3 py-2'}`}
    >
      <span className={collapsed ? 'opacity-80' : 'flex items-center gap-2.5'}>
        <span className="shrink-0 opacity-80">{icon}</span>
        {!collapsed && <span>{label}</span>}
      </span>
      {!collapsed && <span className="opacity-60 text-[10px]">↗</span>}
    </a>
  );
}


function FooterItem({
  collapsed, onClick, icon, label,
}: {
  collapsed: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      className={`w-full flex items-center gap-2.5 text-xs text-dim hover:text-ink hover:bg-white/[0.03] rounded-md transition
        ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'}`}
    >
      <span className="shrink-0">{icon}</span>
      {!collapsed && <span>{label}</span>}
    </button>
  );
}


// ── Icons ───────────────────────────────────────────────────────────────

function IconHome() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"><path d="M2.5 7.5 L8 3 L13.5 7.5 V13 a0.5 0.5 0 0 1-0.5 0.5 H10 V10 H6 V13.5 H3 a0.5 0.5 0 0 1-0.5-0.5 Z" /></svg>;
}
function IconGrid() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="9" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="2.5" y="9" width="4.5" height="4.5" rx="1" /><rect x="9" y="9" width="4.5" height="4.5" rx="1" /></svg>;
}
function IconBook() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"><path d="M3 3 v10 a1 1 0 0 0 1 1 h9 V4 a1 1 0 0 0 -1 -1 H4 a1 1 0 0 0 -1 1z M5.5 6 h5 M5.5 9 h5" /></svg>;
}
function IconCog() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4"><circle cx="8" cy="8" r="2.2" /><path d="M8 1.5v2 M8 12.5v2 M1.5 8h2 M12.5 8h2 M3.4 3.4l1.4 1.4 M11.2 11.2l1.4 1.4 M3.4 12.6l1.4-1.4 M11.2 4.8l1.4-1.4" strokeLinecap="round" /></svg>;
}
function IconKey() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="11" r="2.5" /><path d="M6.8 9.2 13 3 M11 5l1.5 1.5 M10 6l1.5 1.5" /></svg>;
}
function IconBriefcase() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"><rect x="2" y="5" width="12" height="8.5" rx="1.5" /><path d="M5.5 5V3.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V5" /></svg>;
}
function IconClock() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="5.5" /><path d="M8 4.5V8l2.5 2" /></svg>;
}
function IconRefresh() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M13 4v3h-3 M3 12v-3h3" /><path d="M12.5 7a5 5 0 0 0-9-2 M3.5 9a5 5 0 0 0 9 2" /></svg>;
}
function IconCode() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5.5 4 2 8l3.5 4 M10.5 4 14 8l-3.5 4" /></svg>;
}
function IconLogout() {
  return <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 3.5h-5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h5" /><path d="M11 5.5 13.5 8 11 10.5 M7 8h6.5" /></svg>;
}
function IconMenu() {
  return <svg viewBox="0 0 16 16" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M2.5 4h11 M2.5 8h11 M2.5 12h11" /></svg>;
}
function IconClose() {
  return <svg viewBox="0 0 16 16" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 3 L13 13 M13 3 L3 13" /></svg>;
}
/** Panel-with-handle glyph used by most "collapse sidebar" buttons
 * (Slack, Linear, Notion, Instantly's Unibox in the reference shot).
 * Same icon both ways — visually clear that clicking it toggles the
 * sidebar regardless of current state. */
function IconSidebarToggle({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`}
      fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
    >
      <rect x="2" y="3" width="12" height="10" rx="1.5" />
      <path d="M6 3 V13" />
    </svg>
  );
}
